import { useState } from 'react';
import type { BidDecision } from '../lib/aiAdvisor';
import { sounds } from '../lib/soundEffects';

interface Props {
  decision: BidDecision;
  isMyTurn: boolean;
  canAfford: boolean;
  nextRequired: number;
  onApplyBid: (amount: number) => void;
  onOpenWarRoom?: () => void;
  onOpenSettings?: () => void;
}

export function AIAdvisorCard({
  decision,
  isMyTurn,
  canAfford,
  nextRequired,
  onApplyBid,
  onOpenWarRoom,
  onOpenSettings,
}: Props) {
  const [showDetails, setShowDetails] = useState(false);

  // Verdict style mapping
  const actionConfig = (() => {
    switch (decision.action) {
      case 'STRONG_BUY':
        return {
          bg: 'bg-emerald-950/60 border-emerald-500/80',
          badge: 'bg-emerald-500 text-slate-950 font-black',
          buttonClass: 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold shadow-lg shadow-emerald-500/20',
          icon: '🚀',
        };
      case 'VALUE_BUY':
        return {
          bg: 'bg-teal-950/60 border-teal-500/80',
          badge: 'bg-teal-500 text-slate-950 font-black',
          buttonClass: 'bg-teal-500 hover:bg-teal-400 text-slate-950 font-extrabold shadow-lg shadow-teal-500/20',
          icon: '✅',
        };
      case 'PRICE_ENFORCE':
        return {
          bg: 'bg-amber-950/60 border-amber-500/80',
          badge: 'bg-amber-500 text-slate-950 font-black',
          buttonClass: 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold shadow-lg shadow-amber-500/20',
          icon: '⚡',
        };
      case 'HOLD_HIGH_BID':
        return {
          bg: 'bg-cyan-950/50 border-cyan-500/60',
          badge: 'bg-cyan-500 text-slate-950 font-black',
          buttonClass: 'bg-slate-700 text-slate-300 font-bold',
          icon: '👑',
        };
      case 'PASS_OVERPRICED':
        return {
          bg: 'bg-rose-950/50 border-rose-500/60',
          badge: 'bg-rose-500 text-white font-black',
          buttonClass: 'bg-slate-800 text-rose-400 font-bold border border-rose-700',
          icon: '🛑',
        };
      case 'CANNOT_AFFORD':
      default:
        return {
          bg: 'bg-slate-900 border-slate-700',
          badge: 'bg-slate-700 text-slate-300 font-bold',
          buttonClass: 'bg-slate-800 text-slate-400',
          icon: '⚠️',
        };
    }
  })();

  const handleQuickBid = () => {
    sounds.playBidPlaced();
    onApplyBid(decision.recommendedBid);
  };

  return (
    <div className={`rounded-xl border p-4 mb-4 transition-all duration-200 ${actionConfig.bg}`}>
      {/* Top AI Header */}
      <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-white/10">
        <div className="flex items-center gap-2">
          <span className="text-xl">{actionConfig.icon}</span>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-extrabold text-cyan-400">
                Rob's Special AI Engine
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${actionConfig.badge}`}>
                {decision.confidence}% Conviction
              </span>
            </div>
            <div className="text-sm font-extrabold text-white leading-tight">
              {decision.headline}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1">
          {onOpenWarRoom && (
            <button
              onClick={onOpenWarRoom}
              className="text-xs px-2 py-1 rounded bg-slate-800/80 hover:bg-slate-700 text-cyan-300 border border-cyan-500/40"
              title="Open full AI War Room dashboard"
            >
              🧠 War Room
            </button>
          )}
          {onOpenSettings && (
            <button
              onClick={onOpenSettings}
              className="text-xs p-1.5 rounded bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white"
              title="AI Engine Settings"
            >
              ⚙️
            </button>
          )}
        </div>
      </div>

      {/* Primary Verdict & Value Intelligence Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div className="bg-black/30 rounded-lg p-2 text-center border border-white/5">
          <div className="text-[10px] uppercase text-slate-400 font-semibold">Fair Value</div>
          <div className="text-base font-extrabold text-teal-300">${decision.fairMarketValue}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-2 text-center border border-white/5">
          <div className="text-[10px] uppercase text-slate-400 font-semibold">Max Price Ceiling</div>
          <div className="text-base font-extrabold text-cyan-300">${decision.maxProfitableBid}</div>
        </div>
        <div className="bg-black/30 rounded-lg p-2 text-center border border-white/5">
          <div className="text-[10px] uppercase text-slate-400 font-semibold">Value Surplus</div>
          <div className={`text-base font-extrabold ${decision.surplusValue >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {decision.surplusValue >= 0 ? `+$${decision.surplusValue}` : `-$${Math.abs(decision.surplusValue)}`}
          </div>
        </div>
        <div className="bg-black/30 rounded-lg p-2 text-center border border-white/5">
          <div className="text-[10px] uppercase text-slate-400 font-semibold">Value Efficiency</div>
          <div className="text-base font-extrabold text-amber-300">{decision.winsPerDollar} W/$</div>
        </div>
      </div>

      {/* Executive Tactical Reason */}
      <p className="text-xs text-slate-200 leading-relaxed mb-3">
        {decision.tacticalReason}
      </p>

      {/* Opponent & Portfolio context pill */}
      <div className="flex flex-wrap gap-2 text-[11px] mb-3">
        <span className="px-2 py-0.5 rounded bg-slate-800/90 text-slate-300 border border-slate-700">
          🎯 <strong>Rival Intel:</strong> {decision.opponentContext}
        </span>
        <span className="px-2 py-0.5 rounded bg-slate-800/90 text-slate-300 border border-slate-700">
          🛡 <strong>Portfolio:</strong> {decision.portfolioFit.note}
        </span>
      </div>

      {/* 1-Click Action Bar if it's Rob's turn and AI recommends bidding */}
      {isMyTurn && decision.shouldBid && canAfford && (
        <div className="flex items-center gap-2 pt-2 border-t border-white/10">
          <button
            onClick={handleQuickBid}
            className={`flex-1 py-2 px-3 rounded-lg text-sm flex items-center justify-center gap-2 transition active:scale-[0.98] ${actionConfig.buttonClass}`}
          >
            <span>⚡ Apply Recommended Bid (${decision.recommendedBid})</span>
          </button>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs px-2.5 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300"
          >
            {showDetails ? 'Hide' : 'Details'}
          </button>
        </div>
      )}

      {(!isMyTurn || !decision.shouldBid) && (
        <div className="flex items-center justify-between pt-1 border-t border-white/10 text-[11px] text-slate-400">
          <span>
            {decision.isWorthThePrice ? '💎 Team is worth current price' : '⚠️ Price exceeds maximum value ceiling'}
          </span>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-cyan-400 hover:underline"
          >
            {showDetails ? 'Hide analysis ▲' : 'Detailed analysis ▼'}
          </button>
        </div>
      )}

      {/* Expanded Key Points */}
      {showDetails && (
        <div className="mt-3 pt-2 border-t border-white/10 text-xs text-slate-300 space-y-1.5 animate-fadeIn">
          <div className="font-bold text-white text-[11px] uppercase tracking-wider mb-1">
            Deep Tactical Factors:
          </div>
          {decision.keyPoints.map((point, idx) => (
            <div key={idx} className="flex items-start gap-1.5">
              <span className="text-cyan-400">•</span>
              <span>{point}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
